import { TextField } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { HoldsViewStore } from '../stores/HoldsViewStore'

interface Props {
  viewStore: HoldsViewStore
}

export const HoldsResolveDialog = observer(({ viewStore }: Props) => {
  const { t } = useTranslate()
  const [isLoading, setIsLoading] = useState(false)
  const [comment, setComment] = useState('')

  const type = viewStore.items.find(item => item.id === viewStore.selectedHoldIds?.[0])?.type

  const onResolve = async () => {
    setIsLoading(true)
    try {
      const { selectedHoldIds, items } = viewStore
      const isBulk = selectedHoldIds && selectedHoldIds.length > 1

      if (isBulk) {
        await viewStore.bulkResolve(comment || undefined)
      } else if (selectedHoldIds && selectedHoldIds.length === 1) {
        const hold = items.find(item => item.id === selectedHoldIds[0])
        if (hold) {
          await viewStore.resolve(selectedHoldIds[0], hold.journeyId, comment || undefined)
        }
      }

      const count = selectedHoldIds?.length ?? 0
      appStore.setShowAlert(
        'success',
        t('holds.bulkResolve.success', '{number} {type} {holdText} resolved.', {
          number: count,
          type: t(type ?? 'hold.type'),
          holdText: count > 1 ? t('holds', 'holds') : t('hold', 'hold'),
        }),
      )
      setComment('')
      viewStore.closeDialog()
    } catch {
      appStore.setShowAlert('error', t('holds.resolve.error', 'Failed to resolve hold.'))
    } finally {
      setIsLoading(false)
    }
  }

  const onClose = () => {
    setComment('')
    setIsLoading(false)
    viewStore.closeDialog()
  }

  return (
    <ConfirmationDialog
      key={viewStore.selectedHoldIds?.join(',')}
      open={viewStore.isDialogOpen}
      isLoading={isLoading}
      title={t('resolveHold', 'Resolve {type} hold', {
        type: t(type ?? 'hold.type'),
      })}
      primaryActionText={t('yesResolve', 'Yes, resolve')}
      closeLabel={t('noCancel', 'No, cancel')}
      formId='hold-resolve-form'
      onClose={onClose}
      onConfirm={onResolve}
      maxWidth='xs'
    >
      <form>
        <TextField
          id='comment-text-input'
          name='comment'
          multiline
          rows={2}
          value={comment}
          fullWidth
          onChange={e => setComment(e.target.value)}
          placeholder={t('comment', 'Comment')}
        />
      </form>
    </ConfirmationDialog>
  )
})
